import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Button, Link, Stack, Typography } from '@mui/material';
import { useSpring, animated } from '@react-spring/web';

import { Strings } from '@app/constants';
import { PATH_DASHBOARD } from '@app/routes/paths';
import useResponsive from '@app/hooks/use-responsive';

const OnboardingScreen = () => {
  const navigate = useNavigate();
  const isMobile = useResponsive('down', 'md');

  const [image, setImage] = useState(1);
  const [direction, setDirection] = useState<'left' | 'right'>('right'); // Track slide direction

  const { x } = useSpring({
    x: image,
    config: { tension: 250, friction: 30 },
  });

  const handleNext = () => {
    if (image < 3) {
      setDirection('right');
      setImage(image + 1);
    } else {
      navigate(PATH_DASHBOARD.homeScreen);
    }
  };

  const handlePrev = () => {
    if (image > 1) {
      setDirection('right');
      setImage(image - 1);
    }
  };

  const renderText1 = () => (
    <>
      <Typography variant="h4">{Strings.onboard.whatToCook}</Typography>
      <Typography variant="body2" color="#F2F2F2" sx={{ wordSpacing: 0.5, letterSpacing: 0.5 }}>
        {Strings.onboard.leaveTheThinking}
        <span style={{ fontWeight: 'bold', fontSize: 15 }}>{Strings.onboard.chefs}</span>
        {Strings.onboard.basicIngredients}
      </Typography>
    </>
  );

  const renderText2 = () => (
    <>
      <Typography variant="h5">
        {Strings.onboard.withMe}
        <span style={{ color: '#AF0006' }}>{Strings.onboard.ingredients}</span>
        {Strings.onboard.youHave}
      </Typography>
      <Typography variant="h4">{Strings.onboard.findFood}</Typography>
      <Typography variant="body2" color="#696969" sx={{ wordSpacing: 0.5, letterSpacing: 0.5 }}>
        {Strings.onboard.weLoveFood}
      </Typography>
      <Typography
        variant="body2"
        color="#696969"
        sx={{ wordSpacing: 0.5, letterSpacing: 0.5, mt: 1 }}
      >
        {Strings.onboard.letUsHelp}
        <span style={{ fontWeight: 'bold', fontSize: 15, color: 'black' }}>
          {Strings.onboard.chefs}
        </span>
        {Strings.onboard.thinkOutside}
      </Typography>
    </>
  );

  const renderText3 = () => (
    <>
      <Typography variant="h4">{Strings.onboard.welcome}</Typography>
      <Typography variant="body2" color="#696969" sx={{ wordSpacing: 0.5, letterSpacing: 0.5 }}>
        {Strings.onboard.elevateDining}
        <span style={{ fontWeight: 'bold', fontSize: 15, color: 'black' }}>
          {Strings.onboard.chefs}.
        </span>
      </Typography>
    </>
  );

  const renderText = () => {
    switch (image) {
      case 1:
        return renderText1();
      case 2:
        return renderText2();
      case 3:
        return renderText3();
      default:
        return null;
    }
  };

  return (
    <Stack
      direction="row"
      flex={1}
      overflow="hidden"
      sx={{ position: 'relative', width: '100%', height: '100vh' }}
    >
      <animated.div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          transform: x.to(
            (i) => `translateX(${(i - image) * 100 * (direction === 'left' ? -1 : 1)}%)`
          ),
          background: `url(/assets/on-boarding/onboard${image}.png) center center/cover no-repeat`,
          willChange: 'transform',
        }}
      >
        <Stack width="100%">
          <Stack
            sx={{
              alignSelf: 'center',
              top: '55%',
              textAlign: 'center',
              width: isMobile ? '80%' : '45%',
              position: 'absolute',
            }}
            color={image === 1 ? 'white' : ''}
          >
            {renderText()}
          </Stack>
        </Stack>

        <Stack
          sx={{
            position: 'absolute',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            bottom: 0,
            p: 5,
            px: isMobile ? 5 : 10,
          }}
        >
          <Link color={image === 1 ? 'white' : 'textPrimary'} href={PATH_DASHBOARD.homeScreen}>
            {Strings.onboard.skip}
          </Link>

          <Button
            color="error"
            sx={{
              backgroundColor: 'white',
              boxShadow: image !== 3 ? '0.5px 1px 1px 2px rgba(229, 44, 44, 0.2)' : '',
              flexDirection: 'row',
            }}
            onClick={handleNext}
          >
            {image === 3 && Strings.onboard.startCreation}
            <Icon icon={'icon-park-outline:right'} height={20} />
          </Button>
        </Stack>

        {/* Back Button */}
        {image > 1 && (
          <Button
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <Icon icon={'icon-park-outline:left'} height={20} />
          </Button>
        )}
      </animated.div>
    </Stack>
  );
};

export { OnboardingScreen };
